import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Save, Trash2, ExternalLink, Gauge, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { toastApiError } from '@/lib/errors';
import EmptyState from '@/components/EmptyState';
import SortableTh from '@/components/SortableTh';
import useSortable from '@/lib/useSortable';
import { Checkbox } from '@/components/ui/checkbox';
import useBulkSelect, { bulkDelete } from '@/hooks/useBulkSelect';
import BulkDeleteBar from '@/components/BulkDeleteBar';
import InstrumentFormDialog from '@/components/InstrumentFormDialog';

const REQUIRED_FIELDS = [
  { field: 'name', label: 'Název' },
  { field: 'type', label: 'Typ' },
  { field: 'serialNumber', label: 'Výrobní číslo' },
  { field: 'manufacturer', label: 'Výrobce' },
  { field: 'supplier', label: 'Dodavatel' },
  { field: 'operator', label: 'Provozovatel' },
];

export default function InstrumentRegistryPage() {
  const navigate = useNavigate();
  const [instruments, setInstruments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [instTypes, setInstTypes] = useState([]);
  const [probes, setProbes] = useState([]);  // Pro výpočet probeCount per přístroj
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});
  const [search, setSearch] = useState('');
  // Zakládání přístroje jde přes dialog, který vynucuje i vytvoření sondy
  // (s metodikou) — bez sondy nelze přístroj kalibrovat. Zabrání to vzniku
  // bez-sondových přístrojů, na kterých pak selže send-to-calibration.
  const [createOpen, setCreateOpen] = useState(false);

  const load = () => {
    api.get('/instruments').then(r => setInstruments(r.data)).catch(() => {});
    api.get('/companies').then(r => setCompanies(r.data)).catch(() => {});
    api.get('/instrument-types').then(r => setInstTypes(r.data)).catch(() => {});
    api.get('/probes').then(r => setProbes(r.data)).catch(() => setProbes([]));
  };
  useEffect(() => { load(); }, []);

  const selectInst = (i) => { setSelected(i.id); setForm({ ...i }); };

  // Editace existujícího přístroje jede přes pravý panel (PUT). Zakládání
  // nového jde přes InstrumentFormDialog (přístroj + povinná sonda).
  const isDraft = selected === '__new__';

  const missingRequired = REQUIRED_FIELDS
    .filter(({ field }) => !String(form[field] || '').trim())
    .map(({ label }) => label);

  const save = async () => {
    if (!selected) return;
    if (missingRequired.length > 0) {
      toast.error(`Povinná pole nejsou vyplněná: ${missingRequired.join(', ')}.`);
      return;
    }

    // Soft duplicate serialNumber warning (B6) — pouze kontrola, uložení je povolené.
    // Scope: stejný výrobní číslo + stejný provozovatel = pravděpodobně duplicita.
    if (form.serialNumber && form.operator) {
      const dup = instruments.find(i =>
        i.id !== selected &&
        (i.serialNumber || '') === form.serialNumber &&
        (i.operator || '') === form.operator
      );
      if (dup) {
        const msg =
          `Přístroj s výrobním číslem „${form.serialNumber}" a provozovatelem ` +
          `„${form.operator}" už existuje:\n\n` +
          `${dup.name || '—'} ${dup.type || ''}\n\n` +
          `Opravdu chcete uložit i tento?`;
        if (!window.confirm(msg)) return;
      }
    }

    try {
      if (isDraft) {
        const res = await api.post('/instruments', form);
        load();
        selectInst(res.data);
        toast.success('Přístroj vytvořen');
      } else {
        await api.put(`/instruments/${selected}`, form);
        load();
        toast.success('Uloženo');
      }
    } catch (err) {
      toastApiError(err);
    }
  };

  const remove = async () => {
    if (!selected || isDraft) return;
    // cascade=true → smaže i sondy, kalibrace a master certy (vystavené KL).
    // Metrologická data jsou regulovaná — proto explicitní varování v potvrzení.
    if (!window.confirm('Smazat přístroj včetně všech jeho sond, kalibrací a vystavených kalibračních listů? Tuto akci nelze vzít zpět.')) return;
    try {
      await api.delete(`/instruments/${selected}?cascade=true`);
      setSelected(null);
      load();
      toast.success('Přístroj smazán (vč. sond a kalibrací)');
    } catch (err) {
      toastApiError(err);
    }
  };

  const cancelDraft = () => {
    setSelected(null);
    setForm({});
  };

  const companyName = (id) => companies.find(c => c.id === id)?.name || '';
  // Resolve ID → name, s fallbackem na raw hodnotu pro legacy free-text záznamy.
  const resolveCompany = (v) => (companyName(v) || v || '');
  const filtered = instruments.filter(i => {
    if (!search.trim()) return true;
    const haystack = [
      i.name,
      i.type,
      i.serialNumber,
      resolveCompany(i.manufacturer),
      resolveCompany(i.supplier),
      resolveCompany(i.operator),
      i.notes,
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(search.toLowerCase());
  });

  // Sortable columns.
  const { sorted, sortKey, sortDir, toggleSort } = useSortable(filtered, 'name', 'asc');

  // Počet sond per přístroj (group-by client-side).
  const probeCounts = useMemo(() => {
    const counts = {};
    for (const p of probes) {
      counts[p.instrumentId] = (counts[p.instrumentId] || 0) + 1;
    }
    return counts;
  }, [probes]);

  const sel = useBulkSelect(sorted.map(i => i.id));

  const bulkRemove = async () => {
    const ids = sel.selectedIds;
    // cascade=true → smaže i sondy, kalibrace a master certy přístroje (úklid).
    const { ok, failed } = await bulkDelete(ids, id => api.delete(`/instruments/${id}?cascade=true`));
    if (selected && ids.includes(selected)) { setSelected(null); }
    sel.clear();
    load();
    if (failed) toast.warning(`Smazáno ${ok}, ${failed} selhalo`);
    else toast.success(`Smazáno ${ok} (vč. sond a kalibrací)`);
  };

  return (
    <div data-testid="instrument-registry-page" className="flex gap-4 h-[calc(100vh-7rem)]">
      {/* Left: List */}
      <div className="ct-panel w-96 flex-shrink-0 flex flex-col">
        <div className="ct-panel-header flex items-center justify-between">
          <span>Přístroje</span>
          <button data-testid="add-instrument-btn" onClick={() => setCreateOpen(true)} className="p-1 rounded" style={{ color: 'var(--ct-accent)' }}>
            <Plus className="w-4 h-4" />
          </button>
        </div>
        <div className="p-2 space-y-2">
          <Input data-testid="instrument-search" placeholder="Hledat..." value={search} onChange={e => setSearch(e.target.value)} className="h-8 text-sm" />
          <BulkDeleteBar count={sel.count} onDelete={bulkRemove} onClear={sel.clear} entityLabel="přístrojů (vč. jejich sond a kalibrací)" />
        </div>
        <ScrollArea className="flex-1">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--ct-border)' }}>
                <th className="px-2 py-1.5 w-8">
                  <Checkbox checked={sel.allSelected} onCheckedChange={sel.toggleAll} aria-label="Vybrat vše" />
                </th>
                <SortableTh column="name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>Název</SortableTh>
                <SortableTh column="type" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>Typ</SortableTh>
                <SortableTh column="serialNumber" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>V.č.</SortableTh>
                <th className="px-2 py-1.5 text-xs ct-eyebrow text-right" title="Počet sond">Sond</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(i => {
                const cnt = probeCounts[i.id] || 0;
                return (
                  <tr
                    key={i.id}
                    onClick={() => selectInst(i)}
                    className={`ct-table-row cursor-pointer ${selected === i.id ? 'active' : ''}`}
                  >
                    <td className="px-2 py-1.5 w-8" onClick={e => e.stopPropagation()}>
                      <Checkbox
                        checked={sel.isSelected(i.id)}
                        onCheckedChange={() => sel.toggle(i.id)}
                        aria-label={`Vybrat ${i.name}`}
                      />
                    </td>
                    <td className="px-3 py-1.5">{i.name}</td>
                    <td className="px-3 py-1.5 font-mono text-xs">{i.type}</td>
                    <td className="px-3 py-1.5 font-mono text-xs">{i.serialNumber}</td>
                    <td className="px-2 py-1.5 text-right">
                      {cnt > 0 ? (
                        <span
                          className="inline-block px-1.5 py-0.5 rounded text-xs font-mono"
                          style={{ background: 'rgba(var(--ct-accent-rgb), 0.12)', color: 'var(--ct-accent)' }}
                        >
                          {cnt}
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 text-xs"
                          style={{ color: 'var(--ct-warning)' }}
                          title="Bez sond — přístroj nelze kalibrovat"
                        >
                          <AlertTriangle className="w-3.5 h-3.5" /> 0
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ScrollArea>
      </div>

      {/* Right: Detail */}
      {selected ? (
        <div className="flex-1 overflow-y-auto min-h-0 space-y-4 pr-1">
          <div className="ct-panel">
            <div className="ct-panel-header flex items-center justify-between">
              <span>{isDraft ? 'Nový přístroj (rozpracovaný)' : 'Detail vybraného přístroje'}</span>
              {!isDraft && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => navigate(`/instruments/${selected}`)}
                    className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-opacity hover:opacity-80"
                    style={{ color: 'var(--ct-accent)' }}
                    title="Otevřít read-only detail s historií kalibrací a souvisejícími objednávkami"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Otevřít detail
                  </button>
                  <button data-testid="delete-instrument-btn" onClick={remove} className="p-1 rounded" style={{ color: 'var(--ct-error)' }} title="Smazat přístroj">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
            <div className="p-4 grid grid-cols-2 gap-4">
              <div className="ct-form-field">
                <label className="ct-form-label">Název přístroje</label>
                <select
                  data-testid="instrument-name-select"
                  value={form.name || ''}
                  onChange={e => {
                    // Při změně názvu vynulujeme typ (patří k jiné kategorii)
                    setForm({ ...form, name: e.target.value, type: '' });
                  }}
                  className="h-9 text-sm rounded-md border px-3"
                  style={{ background: 'var(--ct-bg)', borderColor: 'var(--ct-border)', color: 'var(--ct-text)' }}
                >
                  <option value="">— Vyberte —</option>
                  {instTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                </select>
              </div>
              <div className="ct-form-field">
                <label className="ct-form-label">Typ</label>
                {(() => {
                  const selectedTypeGroup = instTypes.find(t => t.name === form.name);
                  const availableTypes = selectedTypeGroup?.types || [];
                  if (!form.name) {
                    return (
                      <div
                        className="h-9 flex items-center px-3 rounded-md text-sm"
                        style={{ background: 'var(--ct-bg)', border: '1px solid var(--ct-border)', color: 'var(--ct-text-disabled)' }}
                      >
                        Nejdřív vyberte název
                      </div>
                    );
                  }
                  if (availableTypes.length === 0) {
                    // Fallback: název nemá definované typy — necháme free text
                    return (
                      <Input
                        data-testid="instrument-type-input"
                        value={form.type || ''}
                        onChange={e => setForm({ ...form, type: e.target.value })}
                        className="h-9 text-sm"
                        placeholder="Název nemá definované typy (volný text)"
                      />
                    );
                  }
                  return (
                    <select
                      data-testid="instrument-type-select"
                      value={form.type || ''}
                      onChange={e => setForm({ ...form, type: e.target.value })}
                      className="h-9 text-sm rounded-md border px-3"
                      style={{ background: 'var(--ct-bg)', borderColor: 'var(--ct-border)', color: 'var(--ct-text)' }}
                    >
                      <option value="">— Vyberte typ —</option>
                      {availableTypes.map((t, i) => (
                        <option key={i} value={t.typeName}>
                          {t.typeName}{t.note ? ` (${t.note})` : ''}
                        </option>
                      ))}
                    </select>
                  );
                })()}
              </div>
              <div className="ct-form-field">
                <label className="ct-form-label">Výrobní číslo</label>
                <Input value={form.serialNumber || ''} onChange={e => setForm({ ...form, serialNumber: e.target.value })} className="h-9 text-sm font-mono" />
              </div>
              {(() => {
                // Filtr dle flagů; legacy fallback pokud žádná firma nemá flag
                const hasAnyFlags = companies.some(
                  c => c.isManufacturer || c.isSupplier || c.isOperator
                );
                const filterByFlag = (flag) =>
                  hasAnyFlags ? companies.filter(c => c[flag]) : companies;
                const manufacturers = filterByFlag('isManufacturer');
                const suppliers = filterByFlag('isSupplier');
                const operators = filterByFlag('isOperator');

                // Pro pole mohou existovat legacy hodnoty (free text ze starších záznamů),
                // které nematch-nou žádný company.id. V takovém případě přidáme option
                // se surovou hodnotou + poznámkou, ať se nic ztratí.
                const renderSelect = (fieldName, label, list, testid) => {
                  const value = form[fieldName] || '';
                  const legacyText =
                    value && !list.find(c => c.id === value) ? value : null;
                  return (
                    <div className="ct-form-field">
                      <label className="ct-form-label">{label}</label>
                      <select
                        data-testid={testid}
                        value={value}
                        onChange={e => setForm({ ...form, [fieldName]: e.target.value })}
                        className="h-9 text-sm rounded-md border px-3"
                        style={{ background: 'var(--ct-bg)', borderColor: 'var(--ct-border)', color: 'var(--ct-text)' }}
                      >
                        <option value="">— Vyberte —</option>
                        {list.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        {legacyText && (
                          <option value={legacyText}>
                            {legacyText} (legacy — vyberte z číselníku)
                          </option>
                        )}
                      </select>
                      {list.length === 0 && !hasAnyFlags && (
                        <p className="text-xs mt-1" style={{ color: 'var(--ct-text-secondary)' }}>
                          Žádná firma v číselníku. Přejděte do Evidence výrobců a přidejte.
                        </p>
                      )}
                      {list.length === 0 && hasAnyFlags && (
                        <p className="text-xs mt-1" style={{ color: 'var(--ct-warning)' }}>
                          Žádná firma nemá označenou roli. V Evidenci výrobců zaškrtněte
                          příslušný flag u firmy.
                        </p>
                      )}
                    </div>
                  );
                };

                return (
                  <>
                    {renderSelect('manufacturer', 'Výrobce', manufacturers, 'instrument-manufacturer-select')}
                    {renderSelect('supplier', 'Dodavatel', suppliers, 'instrument-supplier-select')}
                    {renderSelect('operator', 'Provozovatel', operators, 'instrument-operator-select')}
                  </>
                );
              })()}
              <div className="ct-form-field">
                <label className="ct-form-label">Celkem zakázek</label>
                {(form.totalOrders || 0) > 0 && form.name && form.type ? (
                  <button
                    data-testid="instrument-orders-link"
                    onClick={() => navigate(`/orders?instrumentName=${encodeURIComponent(form.name)}&instrumentType=${encodeURIComponent(form.type)}`)}
                    className="h-9 flex items-center justify-between font-mono text-sm px-3 rounded transition-all hover:brightness-110"
                    style={{
                      background: 'rgba(var(--ct-accent-rgb), 0.08)',
                      border: '1px solid var(--ct-accent)',
                      color: 'var(--ct-accent)',
                      cursor: 'pointer',
                    }}
                    title="Zobrazit objednávky obsahující tento přístroj"
                  >
                    <span>{form.totalOrders}</span>
                    <ExternalLink className="w-3 h-3" />
                  </button>
                ) : (
                  <div
                    className="h-9 flex items-center font-mono text-sm px-3 rounded"
                    style={{ background: 'var(--ct-bg)', border: '1px solid var(--ct-border)', color: 'var(--ct-text-disabled)' }}
                  >
                    {form.totalOrders || 0}
                  </div>
                )}
              </div>
              <div className="ct-form-field col-span-2">
                <label className="ct-form-label">Poznámka</label>
                <Textarea value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} className="text-sm" rows={2} />
              </div>
            </div>
            <div className="px-4 pb-4 flex items-center gap-2">
              <Button
                data-testid="save-instrument-btn"
                onClick={save}
                disabled={missingRequired.length > 0}
                className="ct-btn-primary gap-1"
                title={missingRequired.length > 0 ? `Chybí povinná pole: ${missingRequired.join(', ')}` : undefined}
              >
                <Save className="w-4 h-4" /> {isDraft ? 'Vytvořit' : 'Uložit'}
              </Button>
              {isDraft && (
                <Button onClick={cancelDraft} className="ct-btn-secondary">
                  Zrušit
                </Button>
              )}
              {missingRequired.length > 0 && (
                <span className="text-xs" style={{ color: 'var(--ct-text-disabled)' }}>
                  Chybí: {missingRequired.join(', ')}
                </span>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <EmptyState
            icon={Gauge}
            title="Vyberte přístroj"
            description="Klikněte na přístroj v seznamu vlevo pro detail a úpravy."
          />
        </div>
      )}

      <InstrumentFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(inst) => {
          load();
          if (inst) selectInst(inst);
        }}
      />
    </div>
  );
}
